import React from 'react';
import { Link } from 'react-router-dom';

import { usePaperDocument } from '../hooks/usePaperDocument';
import './paper.css';

const REPO = 'https://github.com/TheoTime01/Project_DataMining';
const COMMIT = 'd6eb864';

/* Display equations are written as MathML so that they render natively in the
 * document font instead of pulling in a formula-typesetting library. React
 * renders MathML tags, but going through innerHTML keeps the markup verbatim
 * and keeps this file readable. */
const Equation = ({ tag, math }) => (
  <div className="eq">
    <div className="eq-body" dangerouslySetInnerHTML={{ __html: math }} />
    <div className="eq-tag">({tag})</div>
  </div>
);

/* The dominant colour, as the notebook takes it and as it is defined. */
const EQ_DOMINANT = `
<math display="block">
  <msub><mi>c</mi><mi>taken</mi></msub><mo>=</mo>
  <msub><mi>&#x3BC;</mi><mn>4</mn></msub>
  <mspace width="2.5em"/>
  <msub><mi>c</mi><mi>dominant</mi></msub><mo>=</mo>
  <msub><mi>&#x3BC;</mi><mrow>
    <munder><mi>arg&#x2009;max</mi><mi>j</mi></munder>
    <mrow><mo>|</mo>
      <mo>{</mo><mi>i</mi><mo>:</mo>
      <msub><mi>&#x2113;</mi><mi>i</mi></msub><mo>=</mo><mi>j</mi><mo>}</mo>
    <mo>|</mo></mrow>
  </mrow></msub>
</math>`;

/* The target is drawn independently of the features, so the optimal
 * classifier is a coin toss. */
const EQ_BAYES = `
<math display="block">
  <mi>P</mi><mo stretchy="false">(</mo>
  <mi>Y</mi><mo>=</mo><mn>1</mn>
  <mo>&#x2223;</mo><mi mathvariant="bold">X</mi><mo>=</mo><mi mathvariant="bold">x</mi>
  <mo stretchy="false">)</mo>
  <mo>=</mo>
  <mi>P</mi><mo stretchy="false">(</mo><mi>Y</mi><mo>=</mo><mn>1</mn><mo stretchy="false">)</mo>
  <mo>=</mo>
  <mfrac><mn>1</mn><mn>2</mn></mfrac>
  <mspace width="2em"/>
  <mo>&#x27F9;</mo>
  <mspace width="2em"/>
  <msup><mi>R</mi><mo>&#x2217;</mo></msup>
  <mo>=</mo>
  <mfrac><mn>1</mn><mn>2</mn></mfrac>
</math>`;

/* What a fully grown tree can score on the frame it was fit to: the majority
 * label inside every group of identical feature rows. */
const EQ_CEILING = `
<math display="block">
  <mi>A</mi><mo>=</mo>
  <mfrac><mn>1</mn><mi>N</mi></mfrac>
  <munder><mo>&#x2211;</mo><mrow><mi>g</mi><mo>&#x2208;</mo><mi>G</mi></mrow></munder>
  <mi>max</mi>
  <mo stretchy="false">(</mo>
  <msub><mi>k</mi><mi>g</mi></msub><mo>,</mo>
  <msub><mi>m</mi><mi>g</mi></msub><mo>&#x2212;</mo><msub><mi>k</mi><mi>g</mi></msub>
  <mo stretchy="false">)</mo>
  <mo>,</mo><mspace width="1.5em"/>
  <msub><mi>k</mi><mi>g</mi></msub><mo>&#x223C;</mo>
  <mi>Bin</mi><mo stretchy="false">(</mo>
  <msub><mi>m</mi><mi>g</mi></msub><mo>,</mo>
  <mfrac><mn>1</mn><mn>2</mn></mfrac>
  <mo stretchy="false">)</mo>
</math>`;

/* ------------------------------------------------------------------ *
 * Figure: the pipeline as built.
 *
 * Drawn inline rather than exported from a tool so that it stays editable
 * alongside the prose. Both diagrams sit on a white plate (`fig-plate`) so
 * that the black strokes survive the document's dark mode.
 * ------------------------------------------------------------------ */

const NODES = [
  { x: 20,  y: 14,  w: 210, h: 48, lines: ['pokemon_jpg/', '819 JPEG, 256 × 256'] },
  { x: 270, y: 14,  w: 230, h: 48, lines: ['data_csv/Pokemon.csv', '800 rows, # up to 721'] },
  { x: 20,  y: 98,  w: 210, h: 48, lines: ['MiniBatchKMeans(k = 5)', 'takes cluster_centers_[4]'] },
  { x: 110, y: 186, w: 300, h: 48, lines: ['database.json', 'one record per image'] },
  { x: 110, y: 270, w: 300, h: 48, lines: ['filter_images(), 100 users', 'both types must match'] },
  { x: 110, y: 354, w: 300, h: 52, lines: ['user.json: 1028 rows', 'result: a coin flip, ignoring the features'], heavy: true },
  { x: 0,   y: 442, w: 142, h: 48, lines: ['DecisionTree', 'fit on all 1028'] },
  { x: 152, y: 442, w: 142, h: 48, lines: ['RandomForest(5)', 'fit on 822'] },
  { x: 304, y: 442, w: 156, h: 48, lines: ['Dense 5-64-32-1', 'fit on 822'] },
  { x: 110, y: 530, w: 300, h: 48, lines: ['three .pkl files', 'the LabelEncoders are not saved'] },
  { x: 110, y: 614, w: 300, h: 52, lines: ['recommend_pokemon()', 'refits encoders: every query is [0,0,0,0,0]'], heavy: true },
  { x: 110, y: 702, w: 300, h: 48, lines: ['recommendations printed'] }
];

/* Straight drops down the spine of the diagram. */
const DROPS = [
  [125, 62, 125, 98],
  [260, 234, 260, 270],
  [260, 318, 260, 354],
  [260, 578, 260, 614],
  [260, 666, 260, 702]
];

/* Elbowed joins, and the fan in and out of the three models. */
const PATHS = [
  'M 385 62 L 385 170 L 330 170 L 330 186',
  'M 125 146 L 125 170 L 190 170 L 190 186',
  'M 260 406 L 260 424 L 71 424 L 71 442',
  'M 260 406 L 260 424 L 223 424 L 223 442',
  'M 260 406 L 260 424 L 382 424 L 382 442',
  'M 71 490 L 71 512 L 260 512 L 260 530',
  'M 223 490 L 223 512 L 260 512 L 260 530',
  'M 382 490 L 382 512 L 260 512 L 260 530'
];

const PipelineDiagram = () => (
  <svg
    viewBox="0 0 700 770"
    role="img"
    aria-label={
      'Data flow of the Pokemon recommender: two Kaggle datasets are joined into ' +
      'database.json, a simulated user population attaches coin-flip labels in ' +
      'user.json, three models are fitted and pickled without their label encoders, ' +
      'and the recommender re-fits encoders per query so every query encodes to the ' +
      'zero vector, while the recommendation itself bypasses the models entirely.'
    }
    style={{ width: '100%', height: 'auto', fontFamily: 'inherit' }}
  >
    <defs>
      <marker id="pk-arrow" viewBox="0 0 10 10" refX="9" refY="5"
              markerWidth="7" markerHeight="7" orient="auto-start-reverse">
        <path d="M 0 0 L 10 5 L 0 10 z" fill="#111" />
      </marker>
    </defs>

    {DROPS.map(([x1, y1, x2, y2]) => (
      <line key={`d${x1}-${y1}`} x1={x1} y1={y1} x2={x2} y2={y2}
            stroke="#111" strokeWidth="1.2" markerEnd="url(#pk-arrow)" />
    ))}

    {PATHS.map((d) => (
      <path key={d} d={d} fill="none" stroke="#111" strokeWidth="1.2"
            markerEnd="url(#pk-arrow)" />
    ))}

    {NODES.map((n) => (
      <g key={`${n.x}-${n.y}`}>
        <rect x={n.x} y={n.y} width={n.w} height={n.h} rx="3"
              fill="#fff" stroke="#111" strokeWidth={n.heavy ? 2.2 : 1.2} />
        <text x={n.x + n.w / 2} y={n.y + (n.lines[1] ? 21 : 29)}
              textAnchor="middle" fontSize="12.5" fill="#111">
          {n.lines[0]}
        </text>
        {n.lines[1] && (
          <text x={n.x + n.w / 2} y={n.y + 38} textAnchor="middle"
                fontSize="10.5" fill="#444">
            {n.lines[1]}
          </text>
        )}
      </g>
    ))}

    {/* The path the query actually takes: straight from the database to the
      * output, never through a fitted model. */}
    <path d="M 410 210 L 530 210 L 530 726 L 410 726"
          fill="none" stroke="#111" strokeWidth="1.2"
          strokeDasharray="5 4" markerEnd="url(#pk-arrow)" />
    {['the recommendation never', 'passes through a model:', 'an exact-match filter on',
      'colour, type and generation'].map((line, i) => (
      <text key={line} x={544} y={392 + i * 16} fontSize="11" fill="#111">{line}</text>
    ))}
  </svg>
);

/* ------------------------------------------------------------------ *
 * Figure: the resubstitution ceiling.
 *
 * One series, so no legend: the caption names it. The two reported figures are
 * direct-labelled, the chance line is the only dashed rule (it is a threshold,
 * not grid), and every plotted value is also tabulated in Table 4, so nothing
 * here depends on a tooltip the printed page would not have.
 * ------------------------------------------------------------------ */

/* Mean rows per distinct feature tuple -> expected accuracy on the frame the
 * model was fitted to, for coin-flip labels. Derived; see §7.3. */
const CEILING = [
  [1.00, 0.8370], [1.05, 0.8317], [1.10, 0.8267], [1.15, 0.8221], [1.20, 0.8175],
  [1.25, 0.8132], [1.30, 0.8088], [1.35, 0.8047], [1.40, 0.8007], [1.50, 0.7931],
  [1.75, 0.7763], [2.00, 0.7619], [2.50, 0.7384], [3.00, 0.7199], [4.00, 0.6928],
  [6.00, 0.6593]
];

const PX = { l: 74, r: 586, t: 40, b: 300 };
const sx = (m) => PX.l + ((m - 1) / 5) * (PX.r - PX.l);
const sy = (a) => PX.b - ((a - 0.5) / 0.4) * (PX.b - PX.t);

const CeilingChart = () => (
  <svg
    viewBox="0 0 700 360"
    role="img"
    aria-label={
      'Line chart. Expected accuracy on the training frame falls from 0.84 to 0.66 ' +
      'as the mean number of rows per distinct feature tuple rises from 1 to 6. The ' +
      'two accuracies reported in the project README, 0.8142 and 0.8114, sit on the ' +
      'curve at a multiplicity near 1.25. A dashed rule marks the 0.5 chance level, ' +
      'which is what held-out accuracy measures at every multiplicity.'
    }
    style={{ width: '100%', height: 'auto', fontFamily: 'inherit' }}
  >
    {/* Recessive solid grid, one shade off the plate. */}
    {[0.5, 0.6, 0.7, 0.8, 0.9].map((a) => (
      <g key={a}>
        <line x1={PX.l} y1={sy(a)} x2={PX.r} y2={sy(a)} stroke="#d6d6d6" strokeWidth="0.6" />
        <text x={PX.l - 9} y={sy(a) + 4} textAnchor="end" fontSize="12" fill="#555">
          {a.toFixed(1)}
        </text>
      </g>
    ))}
    {[1, 2, 3, 4, 5, 6].map((m) => (
      <text key={m} x={sx(m)} y={PX.b + 19} textAnchor="middle" fontSize="12" fill="#555">
        {m}
      </text>
    ))}

    <line x1={PX.l} y1={PX.t} x2={PX.l} y2={PX.b} stroke="#111" strokeWidth="1" />
    <line x1={PX.l} y1={PX.b} x2={PX.r} y2={PX.b} stroke="#111" strokeWidth="1" />

    {/* Chance. The one dashed rule in the figure, because it is a threshold. */}
    <line x1={PX.l} y1={sy(0.5)} x2={PX.r} y2={sy(0.5)}
          stroke="#111" strokeWidth="1.2" strokeDasharray="6 4" />
    <text x={PX.r} y={sy(0.5) - 8} textAnchor="end" fontSize="12.5" fill="#111">
      chance = 0.5, and what every hold-out measurement returns
    </text>

    <polyline
      points={CEILING.map(([m, a]) => `${sx(m)},${sy(a)}`).join(' ')}
      fill="none" stroke="#111" strokeWidth="2" />

    {/* The two reported figures, direct-labelled rather than left to a tooltip. */}
    {[[1.23, 0.8142, 'decision tree, 837/1028 = 0.8142'],
      [1.28, 0.8114, 'random forest, 667/822 = 0.8114']].map(([m, a, label], i) => (
      <g key={label}>
        <circle cx={sx(m)} cy={sy(a)} r="5.5" fill="#111" stroke="#fff" strokeWidth="2" />
        <line x1={sx(m) + 7} y1={sy(a)} x2={272} y2={72 + i * 18}
              stroke="#111" strokeWidth="0.7" />
        <text x={278} y={76 + i * 18} fontSize="12.5" fill="#111">{label}</text>
      </g>
    ))}

    <text x={(PX.l + PX.r) / 2} y={PX.b + 40} textAnchor="middle" fontSize="12.5" fill="#111">
      mean rows per distinct feature tuple
    </text>
    <text x="18" y={(PX.t + PX.b) / 2} fontSize="12.5" fill="#111"
          transform={`rotate(-90 18 ${(PX.t + PX.b) / 2})`} textAnchor="middle">
      accuracy on the fitted frame
    </text>
  </svg>
);

const PokemonRecommenderProject = () => {
  const { theme, toggleTheme } = usePaperDocument('/projects/pokemon');

  return (
    <article className="paper-doc">
      <div className="doc-bar">
        <Link to="/">&larr; Théotime Perrichet</Link>
        <button type="button" onClick={toggleTheme}>
          {theme === 'dark' ? ': Light :' : ': Dark :'}
        </button>
      </div>

      <h1>
        Eighty-one percent of a coin flip: a Pokémon recommender measured
        against its own training signal
      </h1>

      <p className="author">
        Théotime Perrichet &nbsp;&middot;&nbsp; Lorenzo Tuzio
      </p>
      <p className="doc-affiliation">
        Data mining project, M.Eng. Robotics, CPE Lyon
      </p>
      <p className="doc-dateline">Lyon, France · March 2023</p>

      <ul className="doc-links">
        <li><a href={REPO}>Source code</a></li>
        <li><a href={`${REPO}/blob/master/projet.ipynb`}>The notebook</a></li>
      </ul>

      <div className="abstract">
        <h2>Abstract</h2>
        <p>
          This report documents an image recommendation system built for a data
          mining course: 819 Pokémon sprites are joined against a Kaggle
          attribute table, a dominant colour is extracted from each image by
          k-means, a population of users is simulated, and three classifiers, a
          decision tree, a five-tree random forest and a small dense network,
          are fitted to predict whether a user will like a given Pokémon. The
          project reports accuracies of 0.8142 and 0.8114 and concludes that
          two of its three models are &ldquo;quite reliable&rdquo;. The
          engineering claim of this write-up is that those numbers do not mean
          what the report takes them to mean, and that the repository already
          contains the evidence. The training target is drawn by{' '}
          <code>randint</code>, independently of every feature, so the Bayes
          rate of the task is exactly one half and no model can generalise above
          chance. Both reported accuracies are resubstitution scores, measured
          on the frame the model was fitted to, and both are numerically
          pinned by the rate at which identical feature vectors repeat in that
          frame rather than by anything either model learned. We recover the
          exact denominators from the published figures (1028 rows for the tree,
          its own 822-row training split for the forest), bracket both values
          with a reconstruction driven by label noise alone, and note that the one honest
          measurement in the project, the validation curve the network plots
          over 50 epochs, sits flat at chance and is read by the report as
          evidence that the network is the weakest of the three. A separate
          defect closes the loop at inference: the recommender constructs fresh{' '}
          <code>LabelEncoder</code>s and fits them to a single-row frame, so
          every query, whatever the user types, encodes to the zero vector and
          every model returns the same answer. The recommendations that reach
          the screen come from a hand-written filter that never consults a
          model. Twenty-two findings are reported from a read of the source.
        </p>
        <p className="keywords">
          <strong>Keywords:</strong> recommender systems · data mining ·
          label leakage · resubstitution error · scikit-learn ·
          k-means colour quantisation · code review · reproducibility
        </p>
      </div>

      <nav aria-label="Table of contents">
        <ol>
          <li><a href="#s1">Introduction</a></li>
          <li><a href="#s2">The corpus and its metadata</a></li>
          <li><a href="#s3">Dominant colour by k-means</a></li>
          <li><a href="#s4">The simulated preference corpus</a></li>
          <li><a href="#s5">The feature frame</a></li>
          <li><a href="#s6">The three models</a></li>
          <li><a href="#s7">Evaluation, measured</a></li>
          <li><a href="#s8">The recommender at inference</a></li>
          <li><a href="#s9">Defect inventory</a></li>
          <li><a href="#s10">Results</a></li>
          <li><a href="#s11">What would have to change</a></li>
          <li><a href="#s12">Reproducing the notebook</a></li>
        </ol>
      </nav>

      <h2 id="s1">1. Introduction</h2>
      <p>
        A recommender is a claim about people: given what somebody liked before,
        here is what they will like next. The claim is testable, and the test is
        the only part of the system that carries any weight, because an
        accuracy figure is not a description of a model, it is a measurement,
        and like any
        measurement it is worth exactly what its procedure is worth.
      </p>
      <p>
        The brief for this project asked for a recommendation system over an
        image collection, built on a public dataset, using data mining models
        taught in the course. The domain chosen was Pokémon: 819 sprites from
        Kaggle <a href="#r2">[2]</a>, joined against the well-known Pokémon
        attribute table <a href="#r3">[3]</a>, with a dominant colour extracted
        from each image so that the recommender has something to say that is not
        already in the CSV. A user answers five questions, covering two types,
        a generation, legendary or not, and a favourite colour, and the system
        returns sprites it thinks that user will like.
      </p>
      <p>
        That system was built and it runs. This document reconstructs it from
        the source and then does the thing the original report does not: it
        treats the reported accuracies as measurements and asks what they
        measure. §2 to §6 describe the corpus, the colour extraction, the
        simulated user population, the feature frame and the three models. §7 is
        the measurement itself, and is the centre of the write-up: it recovers
        the sample sizes behind the two published figures, shows that both are
        resubstitution scores, derives the ceiling that label noise imposes on
        such a score, and reproduces both published numbers from that ceiling
        alone. §8 follows the query path at inference and finds that it does not
        reach the models. §9 is an ordinary code review of the rest.
      </p>
      <p>
        Every figure quoted below is read from the repository{' '}
        <a href="#r1">[1]</a> at commit <code>{COMMIT}</code>, which is the only
        commit it has. Derived quantities are marked as derived, and where a
        quantity is derived by simulation the simulation is described well
        enough to repeat. No new experiments were run against the original
        data: the Kaggle inputs are not redistributable and were not downloaded
        for this write-up.
      </p>
      <p>
        One caveat about provenance, because it shapes what follows. All 58 code
        cells in the committed notebook have a null <code>execution_count</code>{' '}
        and carry no stored outputs, since the notebook was cleared before it was
        committed. Not one of the numbers this project reports can therefore be
        read out of the notebook. They survive only as prose in the README and
        as pixels in eleven PNGs, which is why §7 reaches its conclusions
        forensically, by reconstructing denominators from the decimal
        expansions of the published figures.
      </p>

      <h2 id="s2">2. The corpus and its metadata</h2>

      <h3>2.1 Two datasets, joined on a number</h3>
      <p>
        The notebook pulls two Kaggle datasets. The first{' '}
        <a href="#r2">[2]</a> is a directory of Pokémon sprites, which the
        README records as 819 JPEG images of 32.0&nbsp;MB, each 256 × 256. The
        second <a href="#r3">[3]</a> is a 800-row attribute table whose{' '}
        <code>#</code> column is the Pokédex number and whose rows carry a name,
        one or two types, a generation and a legendary flag.
      </p>
      <p>
        A first pass walks the image directory, skips any filename whose stem is
        not a decimal integer, and writes one record per image into{' '}
        <code>database.json</code>. A second pass reads the CSV, keeps six
        columns, and merges them in as a nested <code>tags</code> object.
        Figure&nbsp;1 shows a sprite and the record the pipeline produces for it.
      </p>

      <figure>
        <div className="fig-row">
          <div>
            <div className="fig-plate">
              <img src="/images/pokemon/sample-image.jpg"
                   alt="The Bulbasaur sprite, 256 by 256 pixels on a white background" />
            </div>
            <span className="fig-label"><code>1.jpg</code></span>
          </div>
          <div>
            <div className="fig-plate">
              <img src="/images/pokemon/sample-record.jpg"
                   alt="The JSON record for 1.jpg, showing id, format, size, orientation, creation date, tags and dominant colour" />
            </div>
            <span className="fig-label">its record in <code>database.json</code></span>
          </div>
        </div>
        <figcaption>
          The unit of the corpus, reproduced from the repository. Three of the
          fields on the right are worth noting before §3 gets to the fourth.{' '}
          <code>orientation</code> is computed as{' '}
          <code>{'"landscape" if w > h else "portrait"'}</code>, which has no
          branch for the square case; since every sprite is 256 × 256, the field
          is the constant <code>&ldquo;portrait&rdquo;</code> for all 819 records
          (F5). <code>creation_date</code> comes from{' '}
          <code>os.path.getctime</code>, so it records when the file was
          unzipped on the author&rsquo;s machine, here 13/03/2023, two days
          before the commit, and not any property of the image (F4). And the
          key is <code>Legendary</code>, with a capital, which §5 returns to.
        </figcaption>
      </figure>

      <h3>2.2 The join</h3>
      <p>
        The merge is a nested loop over both dictionaries with no early exit,
        assigning on every match rather than the first:
      </p>
      <pre><code>{`for key in data_d:
    for i in range(len(data_t)):
        if data_d[key]["id"] == data_t[i]["id"]:
            data_d[key]["tags"] = data_t[i]

for key in data_d:
    data_d[key]["tags"].pop("id")`}</code></pre>
      <p>
        Two things follow. The Pokédex number is not a key of the attribute
        table, because mega evolutions and alternate forms repeat a{' '}
        <code>#</code>, which is how 800 rows span numbers only up to 721, so
        where a number
        repeats, the last row encountered wins and the earlier forms are
        silently discarded (F6). And the <code>pop</code> in the second loop is
        unconditional while <code>tags</code> was initialised to the empty
        string, so any image id with no row in the CSV leaves a <code>str</code>{' '}
        where a <code>dict</code> is expected and the cell raises{' '}
        <code>AttributeError</code> (F7). With 819 images numbered against a
        table that stops at 721, that path is not hypothetical; it is guarded by
        nothing but the particular contents of the download.
      </p>

      <h2 id="s3">3. Dominant colour by k-means</h2>
      <p>
        The one attribute the recommender adds to the CSV is colour. Each sprite
        is flattened into an <em>n</em> × 3 array of RGB pixels, 65 536 of them
        at 256 × 256, and clustered:
      </p>
      <pre><code>{`kmeans = MiniBatchKMeans(n_clusters=5, random_state=0).fit(pixel_data)
dominant_color = kmeans.cluster_centers_[4]`}</code></pre>
      <p>
        The second line is the defect (F8). <code>cluster_centers_</code>{' '}
        <a href="#r5">[5]</a> is ordered by initialisation, not by cluster
        population, and mini-batch k-means <a href="#r6">[6]</a> gives no
        meaning to a centroid&rsquo;s position in that array, so index 4 is
        simply the fifth centroid the algorithm happened to produce. The
        dominant colour is the centroid of the <em>largest</em> cluster, which
        is an <code>argmax</code> over the label histogram and not a constant
        index:
      </p>
      <Equation tag="1" math={EQ_DOMINANT} />
      <p>
        where <code>ℓ</code> is <code>kmeans.labels_</code>. The corrected line
        is <code>cluster_centers_[np.bincount(kmeans.labels_).argmax()]</code>.
        What the notebook computes instead is one arbitrary colour out of five,
        stable across runs, because <code>random_state</code> is fixed, but
        unrelated to how much of the image it covers.
      </p>
      <p>
        There is an irony worth recording rather than glossing. These sprites
        sit on a white field, so the genuinely dominant cluster is nearly always
        the background; a correct implementation of &ldquo;dominant
        colour&rdquo; would return white or near-white for most of the 819
        images and would be a worse feature than the buggy one. The record in
        Figure&nbsp;1 gives Bulbasaur <code>darkseagreen</code>, which is a
        defensible answer arrived at by an indefensible route. The fix that
        matters is therefore not the <code>argmax</code> on its own but masking
        the background first, and neither is present. Nor is the material to do
        it with: the download in cell 6 unpacks two image directories, cell 7
        deletes one of them outright (<code>shutil.rmtree('./pokemon')</code>),
        and everything downstream reads <code>pokemon_jpg/</code>. JPEG carries
        no alpha channel, so by the time the clustering runs the background is
        indistinguishable from a white pixel belonging to the sprite.
      </p>
      <p>
        The centroid is then converted to hex and named. Exact hits go through{' '}
        <code>webcolors.hex_to_name</code>; everything else falls to a
        nearest-neighbour search over the 147 CSS3 names{' '}
        <a href="#r9">[9]</a>, minimising squared distance in RGB. That is not a
        perceptual metric, since equal RGB distances
        are not equal perceived differences, so the naming is systematically
        skewed in the green-dominant region of the cube where these sprites
        mostly live (F9). The search also rebuilds the reference table on every
        one of the 819 calls (F10).
      </p>

      <h2 id="s4">4. The simulated preference corpus</h2>
      <p>
        No real users were available, so the notebook simulates a hundred of
        them. Each draws a first type, a second type and a legendary flag,
        keeps the Pokémon matching that combination, and records a like or a
        dislike for each:
      </p>
      <pre><code>{`while i < nb_user:
    Type1 = choice(Type_t)
    Type2 = choice(Type_t)
    legendary = choice(legendary_t)
    data = filter_images(data_t, Type1, Type2, legendary)
    if len(data) > 0:
        for k in range(len(data)):
            result.append(favorite_t[randint(0, len(favorite_t)-1)])
        all_user[i] = {"data": data, "result": result}
        i += 1`}</code></pre>
      <p>
        The third line from the bottom is the load-bearing one, and everything in
        §7 follows from it. <code>favorite_t</code> is{' '}
        <code>["NoFavorite", "Favorite"]</code>, so{' '}
        <code>randint(0, 1)</code> assigns a like or a dislike by a fair coin.
        The draw does not read <code>data[k]</code>. It does not read the type,
        the generation, the legendary flag or the colour. The label is
        independent of the features by construction, which makes the conditional
        distribution constant and fixes the Bayes rate of the task{' '}
        <a href="#r8">[8]</a> at one half:
      </p>
      <Equation tag="2" math={EQ_BAYES} />
      <p>
        This is not a subtle statistical objection; it is the definition of the
        data. There is no relationship between a Pokémon&rsquo;s attributes and
        whether the simulated user liked it, because none was put there. No
        classifier, not a tree, not a forest, not a network, not one that has
        yet to be invented, can score above 0.5 on unseen rows from this
        generator. §7 shows that the project&rsquo;s own network measured
        exactly that and that the report did not recognise it.
      </p>
      <p>
        Two further properties of this loop matter later.{' '}
        <code>filter_images</code> returns references into the in-memory
        database rather than copies, and mutates them on the way out
        (<code>tags["color"] = color</code>), so the same dictionary object is
        appended once for every user whose draw matched it (F13). And the
        filter requires <em>both</em> drawn types to appear on the Pokémon,
        which is a much narrower predicate than the one the recommender uses at
        inference (F25, §8.4). The consequence of the first is that the training
        frame is a multiset with real repetition, and §7.3 measures how much;
        that measurement turns out to be the whole explanation of the reported
        accuracies.
      </p>

      <h2 id="s5">5. The feature frame</h2>
      <p>
        The three model sections each rebuild the same frame from{' '}
        <code>user.json</code>:
      </p>
      <pre><code>{`dataframe = pd.DataFrame(data, columns=["Type 1", "Type 2", "Generation", "legendary", "color"])
dataframe["legendary"] = le4.fit_transform(dataframe["legendary"])`}</code></pre>
      <p>
        The records in <code>data</code> come from the CSV via §2.2, so their
        keys are <code>Name</code>, <code>Type 1</code>, <code>Type 2</code>,{' '}
        <code>Generation</code>, <code>Legendary</code> and the injected{' '}
        <code>color</code>. The frame asks for <code>legendary</code>, in lower
        case. There is no such key. Pandas fills the column with{' '}
        <code>NaN</code>, <code>LabelEncoder</code> finds a single class and maps
        every row to 0, and no exception is raised anywhere along the way:
      </p>
      <pre><code>{`>>> df = pd.DataFrame(tags, columns=["Type 1","Type 2","Generation","legendary","color"])
>>> df["legendary"].isna().all()
True
>>> LabelEncoder().fit_transform(df["legendary"])
array([0, 0, 0])`}</code></pre>
      <p>
        One of the five features is a constant zero column (F14). The models are
        fitted on four features, not five, and the legendary flag, the one
        attribute a Pokémon fan would call decisive, reaches none of them. The
        network&rsquo;s first layer still declares{' '}
        <code>input_dim=5</code> and still allocates 64 weights for that input,
        all of which train against a column that never varies.
      </p>
      <p>
        The remaining categorical features are integer-coded with{' '}
        <code>LabelEncoder</code>, which assigns codes in sorted order and so
        imposes a total order on nominal categories (F15). A tree then splits
        that order with <code>≤</code>. The root of the fitted tree, read off the
        published render in Figure&nbsp;4, is <code>color ≤ 9.5</code>: a
        threshold on the alphabetical rank of a CSS colour name. Nothing about
        that is meaningful, though with a random target nothing about it is
        harmful either: it is the right criticism of the wrong model, and it
        would matter a great deal if §4 were fixed and this were not.
      </p>

      <figure>
        <div className="fig-plate">
          <img src="/images/pokemon/architecture.png"
               alt="Hand-drawn architecture diagram: sources feed acquisition, annotation and cleaning; a database of JSON, CSV and images; k-means clustering produces dominant colours; user preferences enter a classification block listing SVC, Perceptron, MLPClassifier, Decision Tree and Random Forest, which outputs preferred and non-preferred images" />
        </div>
        <figcaption>
          The design, as the project publishes it at the head of its README and
          as it is reproduced here unaltered. Two things about it are worth
          holding on to while reading Figure&nbsp;3. User preferences enter the{' '}
          <em>classification</em> block and the preferred images come out of it,
          so the model sits on the recommendation path by design, and §8.3 finds
          that it does not sit there as built. And the block offers five
          candidate estimators, of which three were implemented: a decision
          tree, a random forest and, in place of the listed{' '}
          <code>MLPClassifier</code>, a hand-built Keras network.
        </figcaption>
      </figure>

      <figure>
        <div className="fig-plate"><PipelineDiagram /></div>
        <figcaption>
          The system as built, to be read against Figure&nbsp;2. Two heavy
          boxes mark the two places where the
          chain of inference is cut. At <code>user.json</code> the target is
          replaced by a coin flip, which caps generalisation at chance (§4). At{' '}
          <code>recommend_pokemon()</code> the label encoders are rebuilt per
          query and fitted to one row, which collapses every query to the zero
          vector (§8.2), because the encoders fitted during training were never
          persisted alongside the models. The dashed path is the one the user
          actually feels: an exact-match filter over{' '}
          <code>database.json</code> that produces the recommendations without
          consulting any of the three fitted models (§8.3).
        </figcaption>
      </figure>

      <h2 id="s6">6. The three models</h2>
      <p>
        Three classifiers are fitted to the frame of §5. Table&nbsp;1 collects
        what each one is and how it was scored; §7 is about the last column.
      </p>

      <div className="scroll-wrapper">
        <table>
          <caption>
            The three models, their configuration and the frame each reported
            score was computed on. Every score in the last column is measured on
            rows the model had already been fitted to.
          </caption>
          <thead>
            <tr>
              <th>Model</th>
              <th>Configuration</th>
              <th>Fitted on</th>
              <th>Scored on</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><code>DecisionTreeClassifier</code></td>
              <td>All defaults: no depth limit, no pruning, gini</td>
              <td>All 1028 rows</td>
              <td>The same 1028 rows</td>
            </tr>
            <tr>
              <td><code>RandomForestClassifier</code></td>
              <td><code>n_estimators=5</code>, <code>random_state=0</code></td>
              <td>822 rows (an 80% sample)</td>
              <td>The same 822 rows</td>
            </tr>
            <tr>
              <td>Keras <code>Sequential</code></td>
              <td>
                Dense 5-64-32-1, ReLU and sigmoid, Adam,
                binary cross-entropy, 50 epochs, 2 497 parameters
              </td>
              <td>822 rows, with the remaining 206 held out</td>
              <td>
                The same 822 rows, but the held-out 206 are also plotted
                (§7.4)
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>
        The forest and the network both build a held-out split and then decline
        to use it. The pattern is identical in both:
      </p>
      <pre><code>{`train_data   = dataframe.sample(frac=0.8, random_state=0)
test_data    = dataframe.drop(train_data.index)
train_labels = resultframe.sample(frac=0.8, random_state=0)
test_labels  = resultframe.drop(train_labels.index)
...
y_pred = model.predict(train_data)              # not test_data
print("Accuracy:", accuracy_score(train_labels, y_pred))`}</code></pre>
      <p>
        <code>test_data</code> is constructed, is never passed to{' '}
        <code>predict</code>, and the accuracy that reaches the README is the one
        on <code>train_data</code> (F17). The network is blunter still: it calls{' '}
        <code>model.evaluate(train_dataset, train_labels)</code> and prints the
        result under the label <code>Test accuracy</code>.
      </p>
      <p>
        The split itself deserves a note even though it happens to work. Features
        and labels are sampled by two independent calls, and they line up only
        because both frames carry the same index and the same seed. That is a
        true coincidence of the code as written rather than a property of it: any
        upstream reordering of either frame, or a different seed on one of the
        two lines, silently pairs each row with another row&rsquo;s label and
        nothing raises (F18). The idiom that cannot fail this way is one call,{' '}
        <code>train_test_split(X, y, ...)</code>, which takes both frames
        together.
      </p>
      <pre><code>{`>>> X.sample(frac=0.8, random_state=0).index.equals(y.sample(frac=0.8, random_state=0).index)
True                      # today
>>> X.sample(frac=0.8, random_state=0).index.equals(y_reordered.sample(frac=0.8, random_state=0).index)
False                     # after any upstream reorder, and still silent`}</code></pre>

      <h2 id="s7">7. Evaluation, measured</h2>

      <h3>7.1 What the project reports</h3>
      <p>
        The README gives four numbers to sixteen significant figures, which is
        more generosity than it intends but exactly what this section needs:
      </p>
      <div className="scroll-wrapper">
        <table>
          <caption>
            The reported metrics, as printed in the project README, and the
            rational number each decimal expansion resolves to.
          </caption>
          <thead>
            <tr><th>Metric</th><th>As reported</th><th className="num">As a fraction</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>Decision tree, accuracy</td>
              <td><code>0.8142023346303502</code></td>
              <td className="num">837 / 1028</td>
            </tr>
            <tr>
              <td>Decision tree, F1</td>
              <td><code>0.7921653971708378</code></td>
              <td className="num">728 / 919</td>
            </tr>
            <tr>
              <td>Random forest, accuracy</td>
              <td><code>0.8114355231143552</code></td>
              <td className="num">667 / 822</td>
            </tr>
            <tr>
              <td>Random forest, F1 (macro)</td>
              <td><code>0.8114218475952252</code></td>
              <td className="num">6209 / 7652</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p>
        The first and third lines give up the sample sizes directly. The tree was
        scored on <strong>1028 rows</strong> and got 837 of them right. The
        forest was scored on <strong>822</strong> and got 667, and{' '}
        <code>round(0.8 × 1028) = 822</code> exactly, which identifies the
        forest&rsquo;s frame as the 80% sample of the tree&rsquo;s. Both figures
        therefore come from a single execution of the notebook on a single{' '}
        <code>user.json</code> of 1028 rows, and both are resubstitution scores
        on the rows each model was fitted to.
      </p>
      <p>
        The tree&rsquo;s F1 closes the arithmetic. Binary F1 with the positive
        class at 1 is <code>2·TP / (2·TP + FP + FN)</code>; the reported value is
        728/919, so <code>TP = 364</code> and{' '}
        <code>FP + FN = 919 − 728 = 191</code>. With 837 correct out of 1028 the
        errors are also 1028 − 837 = 191, which agrees, and the true negatives
        are 837 − 364 = 473. The full confusion matrix behind the published
        accuracy is therefore recoverable to a single degree of freedom, and it
        balances: 364 + 473 + 191 = 1028.
      </p>

      <h3>7.2 The published figures are three different runs</h3>
      <p>
        Nothing in the notebook is seeded. Cell 31 calls{' '}
        <code>random.choice</code> and <code>random.randint</code> from a
        module-level RNG that is never given a seed, so every execution produces
        a different <code>user.json</code> of a different length (F3). The
        published artefacts bear this out. The rendered tree in Figure&nbsp;4
        states <code>samples = 885</code> at its root; the README&rsquo;s
        accuracy denominator is 1028. The two cannot be the same execution.
      </p>

      <figure>
        <div className="fig-plate">
          <img src="/images/pokemon/tree-root.png"
               alt="The root node of the rendered decision tree: color less than or equal to 9.5, gini 0.5, samples 885, value 429 and 456, class NoFavorite" />
        </div>
        <figcaption>
          The root of the fitted tree, at full resolution from{' '}
          <code>img/decisiontree.png</code>. Three things are legible here and
          all three matter. <code>samples = 885</code> is not the 1028 behind the
          README&rsquo;s accuracy, so the figure and the metrics are different
          runs (§7.2). <code>value = [429, 456]</code> is a 48.5 / 51.5 split,
          and 885 fair coin flips would land this far from even about one time
          in three; <code>gini = 0.5</code> is the maximum attainable for two
          classes, which is what a coin-flip target looks like at the root.
          And the first split of the whole model is a threshold on{' '}
          <code>color</code>, whose integer codes are alphabetical ranks of CSS
          colour names (F15).
        </figcaption>
      </figure>

      <p>
        The root also settles the class polarity that §8 depends on.{' '}
        <code>LabelEncoder</code> sorts its classes, so{' '}
        <code>Favorite</code> encodes to 0 and <code>NoFavorite</code> to 1; the
        node&rsquo;s <code>value = [429, 456]</code> with{' '}
        <code>class = NoFavorite</code> confirms that index 0 is{' '}
        <code>Favorite</code> and the majority is the second class. Both
        confusion matrices in the repository are drawn with{' '}
        <code>display_labels=['not favorite', 'favorite']</code>, which is the
        reverse of that order, so both are labelled backwards (F20).
      </p>

      <h3>7.3 The ceiling that produces 0.81</h3>
      <p>
        A decision tree with no depth limit is grown until its leaves are pure or
        cannot be split further. Since the labels here are independent of the
        features, the only thing that stops a leaf from becoming pure is a group
        of <em>identical</em> feature rows carrying disagreeing labels, and the
        tree can do no better on such a group than predict its majority. The
        accuracy on the fitted frame is therefore not a property of the
        algorithm at all: it is the optimism of the training error rate{' '}
        <a href="#r7">[7]</a> in its most literal form. It is a property of how
        often feature vectors repeat:
      </p>
      <Equation tag="3" math={EQ_CEILING} />
      <p>
        where <em>G</em> is the partition of the frame into groups of identical
        feature rows, <em>m<sub>g</sub></em> is a group&rsquo;s size and{' '}
        <em>k<sub>g</sub></em> its count of one label. Evaluating the expectation
        gives Table&nbsp;3: a group seen once is memorised perfectly, and larger
        groups decay toward chance.
      </p>

      <div className="scroll-wrapper">
        <table>
          <caption>
            The best attainable accuracy on the fitted frame, within one group of{' '}
            <em>m</em> identical feature rows whose labels are fair coin flips.
            Derived from equation (3).
          </caption>
          <thead>
            <tr>
              <th className="num">m</th>
              <th className="num">1</th><th className="num">2</th>
              <th className="num">3</th><th className="num">4</th>
              <th className="num">5</th><th className="num">10</th>
              <th className="num">20</th><th className="num">50</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope="row">E[accuracy]</th>
              <td className="num">1.000</td><td className="num">0.750</td>
              <td className="num">0.750</td><td className="num">0.688</td>
              <td className="num">0.688</td><td className="num">0.623</td>
              <td className="num">0.588</td><td className="num">0.556</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>
        A frame of 1028 rows scoring 0.8142 therefore has a mean multiplicity a
        little above one. That is exactly what §4 builds: the 1028 rows are drawn
        from at most 819 distinct sprites, and after the dead legendary column
        (F14) the distinguishable tuple is only{' '}
        <code>(Type 1, Type 2, Generation, color)</code>, so a handful of sprites
        collapse together and the rest repeat only when two of the hundred users
        drew the same type pair. The lower bound is immediate: 1028 rows over at
        most 819 distinct tuples is a multiplicity of at least 1.26.
      </p>
      <p>
        Figure&nbsp;5 puts the two reported numbers on that curve. Both land
        near a multiplicity of 1.25, within 0.003 of each other, which is the
        second tell, since a decision tree and a five-tree bagged ensemble are
        different estimators with different variance, and they have no reason to
        agree to three decimal places unless neither is estimating anything.
        They agree because they are both reading out the same ceiling.
      </p>

      <figure>
        <div className="fig-plate"><CeilingChart /></div>
        <figcaption>
          Accuracy on the fitted frame against the mean number of rows per
          distinct feature tuple, for a target drawn as a fair coin flip. The
          curve is equation (3) evaluated by simulation over 4 000-row frames;
          the two marked points are the accuracies this project reports; they very nearly
          coincide, which is itself the tell: a single tree and a bagged
          ensemble of five have no reason to agree to three decimal places
          unless neither is estimating anything. Neither point is evidence
          about a model: the curve is the same for the tree, the forest, and
          any estimator that memorises its training frame. The
          dashed rule is the Bayes rate of equation (2), and it is also, to
          within 0.007, what held-out accuracy measures at every multiplicity
          on this curve (Table&nbsp;4). All plotted values are tabulated there.
        </figcaption>
      </figure>

      <p>
        To check that the ceiling really does account for the published figures
        rather than merely bounding them, the pipeline of §4 and §5 was rebuilt
        with the Kaggle attributes replaced by a synthetic corpus of the same
        shape, namely 1028 rows drawn from <em>K</em> distinct tuples, a
        constant legendary column and labels from a fair coin, and scored the
        way the notebook scores. Table&nbsp;4 is the result.
      </p>

      <div className="scroll-wrapper">
        <table>
          <caption>
            A shape-matched reconstruction: 1028 rows over <em>K</em> distinct
            feature tuples, coin-flip labels, scored as the notebook scores.
            Mean of 60 runs per row. The tree and forest columns bracket both
            published figures at <em>K</em> between 800 and 900; the hold-out
            column is chance everywhere. Derived.
          </caption>
          <thead>
            <tr>
              <th className="num">K</th>
              <th className="num">mean multiplicity</th>
              <th className="num">tree, fitted frame</th>
              <th className="num">forest, fitted frame</th>
              <th className="num">tree, held out</th>
            </tr>
          </thead>
          <tbody>
            <tr><td className="num">400</td><td className="num">2.57</td><td className="num">0.7363</td><td className="num">0.7424</td><td className="num">0.5050</td></tr>
            <tr><td className="num">600</td><td className="num">1.71</td><td className="num">0.7792</td><td className="num">0.7791</td><td className="num">0.5032</td></tr>
            <tr><td className="num">700</td><td className="num">1.47</td><td className="num">0.7973</td><td className="num">0.7897</td><td className="num">0.5066</td></tr>
            <tr><td className="num">800</td><td className="num">1.28</td><td className="num">0.8083</td><td className="num">0.7988</td><td className="num">0.5015</td></tr>
            <tr><td className="num">900</td><td className="num">1.14</td><td className="num">0.8245</td><td className="num">0.8133</td><td className="num">0.5005</td></tr>
            <tr><td className="num">1028</td><td className="num">1.00</td><td className="num">0.8360</td><td className="num">0.8146</td><td className="num">0.5025</td></tr>
            <tr>
              <th scope="row" colSpan="2">reported by the project</th>
              <td className="num"><strong>0.8142</strong></td>
              <td className="num"><strong>0.8114</strong></td>
              <td className="num">n/a</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>
        Both published accuracies fall inside the reconstruction&rsquo;s range,
        at the multiplicity the corpus geometry predicts, with no signal in the
        data at all. The held-out column is the point of the table: 0.50 in every
        row, regardless of <em>K</em>, regardless of which model, exactly as
        equation (2) requires.
      </p>

      <h3>7.4 The measurement the project already made</h3>
      <p>
        The strongest evidence for everything above is not the reconstruction.
        It is in the repository, published as <code>img/model2.png</code>, and it
        has been there since the commit.
      </p>
      <p>
        Alone among the three models, the network passes a genuine held-out set
        to <code>fit</code>, as{' '}
        <code>validation_data=(test_dataset, test_labels)</code>, and Keras
        scores it once per epoch. The reported <em>metric</em> still
        comes from <code>evaluate</code> on the training rows (F17), but the
        history object retains the honest series, and the notebook plots it.
      </p>

      <figure>
        <div className="fig-row">
          <div style={{ flex: '0 1 15rem' }}>
            <div className="fig-plate">
              <img src="/images/pokemon/nn-architecture.png"
                   alt="Keras model diagram: input of dimension 5, dense layers of 64 and 32 units, dense output of 1 unit" />
            </div>
            <span className="fig-label">2 497 parameters over 4 live features</span>
          </div>
          <div style={{ flex: '0 1 22rem' }}>
            <div className="fig-plate">
              <img src="/images/pokemon/nn-curves.png"
                   alt="Training curves over 50 epochs: training accuracy drifts up from 0.50 to 0.58, validation accuracy oscillates around 0.50 with no trend, training loss falls slowly, validation loss rises" />
            </div>
            <span className="fig-label">50 epochs, reproduced from the repository</span>
          </div>
        </div>
        <figcaption>
          The network, and the one honest measurement in the project. Values
          below are read back off the published plot against its own axis ticks.
          Training accuracy (blue) starts at chance and drifts up to about 0.57
          as 2 497 parameters begin to memorise 822 rows. Validation accuracy
          (orange) oscillates between 0.47 and 0.55 for fifty epochs, averages
          0.513, and ends where it started. The loss curves are sharper still:
          training loss (green) falls from 0.80 to 0.677, while validation loss
          (red) has a minimum over all fifty epochs of 0.692. The binary
          cross-entropy of a fair coin is exactly ln&nbsp;2 = 0.6931. The
          network&rsquo;s held-out loss never once gets below the entropy of its
          own target, and its training loss does, which is what memorising
          noise looks like, measured. This plot is a correct measurement of the
          task defined in §4, and the value it measures is chance.
        </figcaption>
      </figure>

      <p>
        The README reads this figure and concludes: &ldquo;Except for our neural
        network, our Random Forest and Decision Tree models are quite reliable
        because they are accurate (greater than 0.8).&rdquo; The inference runs
        precisely backwards. The network is not the weak model; it is the only
        model that was measured on data it had not already seen; its answer of
        chance is the correct one for all three. The other two look
        better only because their numbers were taken on the frames they were
        fitted to. The project&rsquo;s ranking of its own models is an artefact
        of which model happened to get an honest evaluation.
      </p>

      <h3>7.5 What the fitted models look like</h3>
      <p>
        The shape of the fitted tree corroborates the reading. It resolves into
        eighteen ranks of nodes, a tree of depth seventeen over four usable
        features, grown until almost every leaf holds a single training row.
      </p>

      <figure>
        <div className="fig-wide">
          <img src="/images/pokemon/tree-extent.png"
               alt="The complete fitted decision tree as a very wide scrolling strip: a fan of several hundred nodes, seventeen levels deep, ending in rows of saturated pure leaves" />
        </div>
        <figcaption>
          The whole fitted tree. The Graphviz render is 21 841 × 2 957 pixels;
          it is reproduced here at a height that keeps the nodes visible, which
          means it scrolls sideways for as long as it takes, and that is the
          observation. Saturation encodes leaf purity, so the deep bands of
          solid blue and orange along the bottom are leaves that have memorised
          individual coin flips. The README notes that &ldquo;the generated trees
          are very large, which means that the models may be over-fitted&rdquo;;
          §7.3 makes that concrete: the size <em>is</em> the accuracy, since a
          leaf per row is what scores 0.81 on a random target.
        </figcaption>
      </figure>

      <figure>
        <div className="fig-row">
          <div style={{ flex: '0 1 19rem' }}>
            <div className="fig-plate">
              <img src="/images/pokemon/tree-confusion.png"
                   alt="Decision tree confusion matrix, row-normalised: 0.81 on both diagonal cells, 0.19 off-diagonal" />
            </div>
            <span className="fig-label">decision tree</span>
          </div>
          <div style={{ flex: '0 1 19rem' }}>
            <div className="fig-plate">
              <img src="/images/pokemon/forest-confusion.png"
                   alt="Random forest confusion matrix, row-normalised: 0.90 and 0.10 on the first row, 0.27 and 0.73 on the second" />
            </div>
            <span className="fig-label">random forest</span>
          </div>
        </div>
        <figcaption>
          Confusion matrices, reproduced from the repository and both computed on
          the fitted frames. The axis labels on both are inverted (F20): the
          encoder sorts <code>Favorite</code> to index 0, so the first row and
          column are <code>Favorite</code>, not <code>not favorite</code>. On the
          tree the error rate is 0.19 in both directions and the swap is
          invisible; on the forest it is not, and the plate as labelled reports
          the opposite of what the model does, since the class recalled at 0.90 is{' '}
          <code>Favorite</code>. The tree&rsquo;s symmetry is itself consistent
          with §7.3: a majority vote over balanced coin flips has no reason to
          prefer either class.
        </figcaption>
      </figure>

      <p>
        A last measurement note. The two F1 figures the README places side by
        side are not the same statistic (F19). The tree&rsquo;s comes from{' '}
        <code>f1_score(y, pred)</code>, which with default arguments is binary F1
        for the positive class, here <code>NoFavorite</code>, since that is the
        class encoded 1. The forest&rsquo;s comes from{' '}
        <code>f1_score(y, pred, average='macro')</code>, the unweighted mean over
        both classes. Comparing 0.7922 to 0.8114 compares a one-class score with
        a two-class average.
      </p>

      <h2 id="s8">8. The recommender at inference</h2>

      <h3>8.1 The query path</h3>
      <p>
        <code>recommend_pokemon()</code> prompts for five values, assembles a
        one-row frame, encodes it, and asks the three unpickled models whether
        this user will like it:
      </p>
      <pre><code>{`le1 = LabelEncoder()          # ... through le5, all freshly constructed
pokemon = pd.DataFrame([[type1, type2, generation, legendary, couleur]],
                       columns=["Type 1", "Type 2", "Generation", "legendary", "color"])
pokemon["Type 1"] = le1.fit_transform(pokemon["Type 1"])
pokemon["Type 2"] = le2.fit_transform(pokemon["Type 2"])
pokemon["Generation"] = le3.fit_transform(pokemon["Generation"])
pokemon["legendary"] = le4.fit_transform(pokemon["legendary"])
pokemon["color"] = le5.fit_transform(pokemon["color"])`}</code></pre>

      <h3>8.2 Every query is the zero vector</h3>
      <p>
        <code>fit_transform</code> fits the encoder to the data it is given and
        then transforms it. The data it is given is one row. A{' '}
        <code>LabelEncoder</code> fitted to a single value has exactly one class,
        and maps that value to 0, whatever the value is:
      </p>
      <pre><code>{`Pikachu    -> [0, 0, 0, 0, 0]
Bulbasaur  -> [0, 0, 0, 0, 0]
Mewtwo     -> [0, 0, 0, 0, 0]`}</code></pre>
      <p>
        Those three are the notebook&rsquo;s own test cases, taken verbatim from
        its final cell; the vectors are what its own encoding block returns for
        them, obtained by running it, since the committed notebook stores no
        outputs of its own (§1). Every query the system can receive
        encodes to the same vector, so all three models return the same
        prediction for every user and every set of answers (F21). The models are
        deterministic; the questions are decorative.
      </p>
      <p>
        The root cause is one line that was never written rather than one that
        was written wrongly. The encoders fitted during training,{' '}
        <code>le1</code> through <code>le5</code>, holding the mapping from type
        and colour names to the integer codes the models were actually trained
        on, are local variables in the training cells. Only the three estimators
        are pickled. By the time the recommender runs, the vocabulary is gone,
        and there is no way for a query to be encoded into the space the models
        learned even in principle. Persisting the fitted encoders alongside the
        models, and calling <code>transform</code> rather than{' '}
        <code>fit_transform</code>, is the whole fix.
      </p>
      <p>
        Two smaller defects sit on top of this one and would bite the moment it
        is fixed. The vote is written as
      </p>
      <pre><code>{`# Si 2 des 3 modèles prédit que le Pokémon sera favori, alors le Pokémon sera favori
if (prediction1[0] == 0 or prediction2[0][0] >= 0.6 or prediction3[0] == 0):`}</code></pre>
      <p>
        The comment describes a majority of three; the code is a three-way{' '}
        <code>or</code>, which fires on any single model (F23). And the middle
        term is inverted relative to the other two (F22): the tree and the forest
        treat class 0 as <code>Favorite</code>, per §7.2, so the network&rsquo;s
        sigmoid, trained with <code>binary_crossentropy</code> against those
        same labels, estimates the probability of class 1, which is{' '}
        <code>NoFavorite</code>. Reading <code>&gt;= 0.6</code> as
        &ldquo;favorite&rdquo; asks the network the opposite of what it asks the
        trees. The threshold 0.6 is itself uncalibrated.
      </p>

      <h3>8.3 The recommendation does not come from a model</h3>
      <p>
        Once the gate passes, the sprites that reach the screen are selected by a
        loop over <code>database.json</code> that compares fields directly:
      </p>
      <pre><code>{`for pokemon_data in data.values():
    if pokemon_data['tags']["Legendary"] == False:
        if (pokemon_data["couleur dominante"] == couleur and
            ((pokemon_data["tags"]["Type 1"] == type1 or ...)) and
            pokemon_data["tags"]["Generation"] == generation):
            recommandations.append(pokemon_data)`}</code></pre>
      <p>
        No model is consulted here, and no score orders the results (F24). This
        is a database query, an exact-match filter on colour, type and
        generation, and it is the only part of the system whose output depends
        on what the user typed. The three classifiers act as a gate in front of
        it whose state, by §8.2, never changes. Whatever the recommender is, it
        is not the architecture of Figure&nbsp;2, and the machine learning in it
        is not load-bearing.
      </p>
      <p>
        The loop around all of this is <code>while not satisfait</code>, which
        re-prompts when the gate rejects. Since the gate is constant, it either
        passes on the first attempt for every user forever or it never passes and
        the function re-asks the same five questions indefinitely (F29). Which of
        the two depends only on the pickles on disk.
      </p>

      <h3>8.4 Where the filter disagrees with the training set</h3>
      <p>
        Even taken on its own terms as a retrieval rule, the filter does not
        match the one that generated the training data. Table&nbsp;5 puts them
        side by side.
      </p>

      <div className="scroll-wrapper">
        <table className="col-findings">
          <caption>
            The same predicate, written twice. The left column built every row
            the models were fitted to; the right column selects what the user
            sees. They are not the same relation.
          </caption>
          <thead>
            <tr>
              <th>Aspect</th>
              <th><code>filter_images()</code>, training</th>
              <th><code>recommend_pokemon()</code>, serving</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Types</td>
              <td>Both drawn types must appear on the Pokémon (a conjunction)</td>
              <td>Either type in either slot suffices (a four-way disjunction)</td>
            </tr>
            <tr>
              <td>Generation</td>
              <td>Not constrained</td>
              <td>Must match exactly</td>
            </tr>
            <tr>
              <td>Colour</td>
              <td>Not constrained; attached to the row afterwards</td>
              <td>Must match exactly</td>
            </tr>
            <tr>
              <td>Legendary</td>
              <td>Must match the drawn flag</td>
              <td>
                Non-legendary queries also filter on type; legendary queries drop
                the type constraint entirely (F26)
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>
        The consequence is train/serve skew in the strict sense (F25): the
        distribution the models were fitted to is generated by a different rule
        from the one that produces the candidates they are asked about. Two
        input-handling defects compound it. <code>legendary</code> arrives from{' '}
        <code>input()</code> as a string and is tested with{' '}
        <code>== "False"</code>, so <code>false</code>, <code>no</code>,{' '}
        <code>f</code> and an empty line all fall through to the legendary branch
        (F27). And <code>Type 2</code> is JSON <code>null</code> for
        single-type Pokémon, rendered as <code>None</code> in the prompt that
        lists the options; a user who types <code>None</code> supplies the
        four-character string, which never equals Python&rsquo;s{' '}
        <code>None</code>, so single-type Pokémon cannot be reached through that
        comparison (F28).
      </p>

      <h2 id="s9">9. Defect inventory</h2>
      <p>
        Twenty-two findings from a read of the source, ordered by the stage of
        the pipeline they sit in. Severity is judged against what the project set
        out to do: <em>critical</em> means the stated result does not follow,{' '}
        <em>major</em> means a documented behaviour is wrong,{' '}
        <em>minor</em> means correctness is unaffected. The five findings marked
        with a dagger were verified by executing the construct in isolation; the
        rest are read from the source.
      </p>

      <div className="scroll-wrapper">
        <table className="col-findings">
          <caption>
            Findings, with the cell or file each is read from. Cell numbers are
            zero-based positions in <code>projet.ipynb</code> at commit{' '}
            <code>{COMMIT}</code>.
          </caption>
          <thead>
            <tr>
              <th>#</th>
              <th>Finding</th>
              <th>Where</th>
              <th>Severity</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>F1</td>
              <td>
                <code>requirements.txt</code> lists <code>pickle</code>, a
                standard-library module with no distribution on PyPI, so{' '}
                <code>pip install -r requirements.txt</code>, the
                notebook&rsquo;s first executable cell, fails outright
              </td>
              <td><code>requirements.txt</code>, cell 2</td>
              <td>major</td>
            </tr>
            <tr>
              <td>F2</td>
              <td>
                <code>webcolors</code> and <code>Pillow</code> are imported but
                not declared; nothing is version-pinned, and the code uses{' '}
                <code>CSS3_NAMES_TO_HEX</code>, a name from the 1.x webcolors API
              </td>
              <td><code>requirements.txt</code>, cell 15</td>
              <td>major</td>
            </tr>
            <tr>
              <td>F3</td>
              <td>
                No RNG is seeded, so every run yields a different corpus. The
                README metrics (1028 rows), the rendered tree (885) and the
                confusion matrices are three different executions
              </td>
              <td>cell 31</td>
              <td>major</td>
            </tr>
            <tr>
              <td>F4</td>
              <td>
                <code>creation_date</code> is <code>os.path.getctime</code>, the
                unzip time on the author&rsquo;s machine, identical for every
                record and not a property of the image
              </td>
              <td>cell 12</td>
              <td>minor</td>
            </tr>
            <tr>
              <td>F5</td>
              <td>
                <code>orientation</code> has no branch for the square case and
                every sprite is 256 × 256, so the field is a constant
              </td>
              <td>cell 12</td>
              <td>minor</td>
            </tr>
            <tr>
              <td>F6</td>
              <td>
                The image-to-CSV join is a nested loop with no early exit that
                assigns on every match; <code>#</code> is not unique across
                alternate forms, so the last matching row silently wins
              </td>
              <td>cell 25</td>
              <td>major</td>
            </tr>
            <tr>
              <td>F7</td>
              <td>
                <code>tags.pop("id")</code> is unconditional but{' '}
                <code>tags</code> is initialised to <code>""</code>; any image id
                missing from the CSV raises <code>AttributeError</code>
              </td>
              <td>cell 25</td>
              <td>major</td>
            </tr>
            <tr>
              <td>F8</td>
              <td>
                <code>cluster_centers_[4]</code> takes the fifth centroid, not
                the largest cluster. MiniBatchKMeans does not order centroids by
                population, so the &ldquo;dominant colour&rdquo; is an arbitrary
                one of five
              </td>
              <td>cell 17</td>
              <td>critical</td>
            </tr>
            <tr>
              <td>F9</td>
              <td>
                Colour naming minimises squared RGB distance, which is not
                perceptually uniform
              </td>
              <td>cell 16</td>
              <td>minor</td>
            </tr>
            <tr>
              <td>F10</td>
              <td>
                <code>closest_color</code> rebuilds the 147-entry CSS3 table on
                every call, once per image
              </td>
              <td>cell 16</td>
              <td>minor</td>
            </tr>
            <tr>
              <td>F11</td>
              <td>
                The pixel reshape hard-codes three channels; a greyscale or RGBA
                sprite raises
              </td>
              <td>cell 17</td>
              <td>minor</td>
            </tr>
            <tr>
              <td>F12 †</td>
              <td>
                <strong>
                  The training target is drawn by <code>randint</code>,
                  independently of every feature.
                </strong>{' '}
                The Bayes rate of the task is 0.5 and no model can generalise
                above chance
              </td>
              <td>cell 31</td>
              <td>critical</td>
            </tr>
            <tr>
              <td>F13</td>
              <td>
                <code>filter_images</code> returns references into the in-memory
                database and mutates them; the same object is appended once per
                matching user
              </td>
              <td>cell 28</td>
              <td>minor</td>
            </tr>
            <tr>
              <td>F14 †</td>
              <td>
                <strong>The <code>legendary</code> feature is dead.</strong> The
                tags key is <code>Legendary</code>; the frame requests{' '}
                <code>legendary</code>, gets an all-<code>NaN</code> column, and{' '}
                <code>LabelEncoder</code> maps it to a constant 0 without raising
              </td>
              <td>cells 40, 53, 66</td>
              <td>critical</td>
            </tr>
            <tr>
              <td>F15</td>
              <td>
                <code>LabelEncoder</code> imposes an alphabetical total order on
                nominal categories, which the trees then split with{' '}
                <code>≤</code>; the root split is a threshold on a colour
                name&rsquo;s rank
              </td>
              <td>cells 40, 53, 66</td>
              <td>major</td>
            </tr>
            <tr>
              <td>F16</td>
              <td>
                The decision tree is fitted and scored on the same 1028 rows,
                with no split at all
              </td>
              <td>cells 40, 44</td>
              <td>critical</td>
            </tr>
            <tr>
              <td>F17</td>
              <td>
                The forest and the network build a held-out split and then score
                the training split; the network prints the result as{' '}
                <code>Test accuracy</code>
              </td>
              <td>cells 58, 72</td>
              <td>critical</td>
            </tr>
            <tr>
              <td>F18 †</td>
              <td>
                Features and labels are split by two independent{' '}
                <code>.sample()</code> calls. They align only because both frames
                share an index and a seed; any upstream reorder pairs rows with
                the wrong labels, silently
              </td>
              <td>cells 53, 66</td>
              <td>major</td>
            </tr>
            <tr>
              <td>F19</td>
              <td>
                The two reported F1 figures are different statistics, binary F1
                on one class for the tree and macro F1 for the forest, and the
                README compares them directly
              </td>
              <td>cells 44, 72</td>
              <td>major</td>
            </tr>
            <tr>
              <td>F20 †</td>
              <td>
                Both confusion matrices are drawn with{' '}
                <code>display_labels=['not favorite', 'favorite']</code>, the
                reverse of the encoder&rsquo;s alphabetical order. The
                forest&rsquo;s plate therefore reads backwards
              </td>
              <td>cells 45, 73</td>
              <td>major</td>
            </tr>
            <tr>
              <td>F21 †</td>
              <td>
                <strong>Every query encodes to the zero vector.</strong> The
                recommender constructs fresh encoders and calls{' '}
                <code>fit_transform</code> on a single row, so all five features
                map to 0 for every input; the training encoders were never
                persisted
              </td>
              <td>cells 80, 86</td>
              <td>critical</td>
            </tr>
            <tr>
              <td>F22</td>
              <td>
                The network&rsquo;s decision is inverted relative to the trees:
                class 0 is <code>Favorite</code>, so the sigmoid estimates{' '}
                <code>NoFavorite</code>, yet <code>&gt;= 0.6</code> is read as
                favourite
              </td>
              <td>cells 80, 86</td>
              <td>major</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>
        Six further items from §8 are not tabulated because they are consequences
        of F21 rather than independent faults: the majority-of-three vote written
        as a disjunction (F23), the models being absent from the retrieval path
        (F24), the train/serve predicate mismatch (F25), the legendary
        branch&rsquo;s missing type constraint (F26), the string comparison
        against <code>"False"</code> (F27), the unreachable single-type
        Pokémon (F28), and the re-prompt loop over a constant gate (F29). Two
        housekeeping notes close the list: <code>visualisation()</code> re-reads
        and re-parses <code>database.json</code> on every call, four times per
        recommended sprite (F30), and the Keras model is persisted with{' '}
        <code>pickle.dump</code> rather than <code>model.save()</code>, which
        ties the artefact to an exact Keras version (F31).
      </p>

      <h2 id="s10">10. Results</h2>
      <p>
        Stated plainly, so that the useful parts are not lost in the defect list.
      </p>
      <p>
        <strong>What the project built.</strong> A complete, working data
        pipeline: two Kaggle datasets acquired through the API, 819 images walked
        and characterised, a k-means colour extractor, a JSON store joining
        image metadata to tabular attributes, three models from three different
        families fitted and serialised, and an interactive console application
        that retrieves and displays matching sprites with per-attribute
        distribution plots. For a semester data-mining project, the breadth is
        real and the plumbing works end to end.
      </p>
      <p>
        <strong>What the reported numbers mean.</strong> Not what the report
        takes them to mean. The 0.8142 and 0.8114 are resubstitution scores on a
        target that is a coin flip, and §7.3 brackets both with a reconstruction
        driven by label noise and duplicate-row multiplicity alone, in which no
        model learns anything.
        The honest figure for all three models is 0.5, which the project itself
        measured and plotted (§7.4) and then attributed to a weakness of the
        neural network.
      </p>
      <p>
        <strong>What the recommender does.</strong> It filters{' '}
        <code>database.json</code> on exact matches of colour, type and
        generation. The three classifiers sit in front of that filter as a gate
        that returns the same verdict for every possible input, because every
        input encodes to the same vector (§8.2). Users of the system are getting
        a database query, correctly executed, with a constant in front of it.
      </p>
      <p>
        <strong>The one measurement that stands.</strong> Figure&nbsp;6 is a
        correct experiment, correctly plotted: it took a held-out set, scored it
        every epoch, and showed a flat line at chance and a held-out loss that
        never fell below the entropy of its own target. Everything this
        write-up argues, that plot already demonstrated. It
        needed a reader who expected 0.5 rather than 0.8.
      </p>
      <p>
        The general lesson is not about Pokémon. Two of the three models were
        never given a chance to fail, and so they did not; the third was, and it
        did, and was marked down for it. A pipeline that cannot report a bad
        result will report a good one, and an accuracy that is never computed on
        unseen data is not a weak measurement but an absent one.
      </p>

      <h2 id="s11">11. What would have to change</h2>
      <p>
        In dependency order, since each item is a precondition for the ones
        below it meaning anything.
      </p>
      <ol>
        <li>
          <strong>Give the target a relationship to the features (F12).</strong>{' '}
          A simulated user needs a preference function: sample a latent taste per
          user, be it a favoured type, a colour or a bias toward legendaries,
          and draw the label from a Bernoulli whose parameter depends on how well the
          Pokémon matches it. That is a handful of lines and it is the difference
          between a dataset and a random number generator. Keep the noise level
          explicit, because it fixes the Bayes rate and so tells you what a good
          score would even look like.
        </li>
        <li>
          <strong>Measure on data the model has not seen (F16, F17).</strong>{' '}
          One{' '}
          <code>train_test_split(X, y, test_size=0.2, stratify=y, random_state=0)</code>{' '}
          replaces the four <code>.sample()</code> lines and removes F18 with
          them. Report the test score. Since users are the unit of
          generalisation here, the split should be by user rather than by row,
          since the same sprite appearing in both halves under two different
          users is a leak in its own right.
        </li>
        <li>
          <strong>Seed everything (F3).</strong> One{' '}
          <code>random.seed(0)</code> next to the existing{' '}
          <code>random_state=0</code>, and the README&rsquo;s numbers become
          reproducible from the notebook instead of recoverable only from their
          decimal expansions.
        </li>
        <li>
          <strong>Fix the feature frame (F14, F15).</strong> Rename the column
          to <code>Legendary</code> and assert that no feature column is
          constant after encoding, a two-line check that would have caught this
          at the moment it was introduced. Replace{' '}
          <code>LabelEncoder</code> on the nominal columns with{' '}
          <code>OneHotEncoder</code>, or with scikit-learn&rsquo;s native
          categorical support, so that no model is asked to threshold an
          alphabetical rank.
        </li>
        <li>
          <strong>Persist the encoders with the models (F21).</strong> Fit a{' '}
          <code>ColumnTransformer</code> inside a <code>Pipeline</code>, pickle
          the pipeline rather than the bare estimator, and the query path gets{' '}
          <code>transform</code> semantics for free, so the class of bug in §8.2
          becomes unexpressible rather than merely fixed.
        </li>
        <li>
          <strong>Fix the dominant colour (F8), then reconsider it.</strong>{' '}
          Equation (1) is the one-line correction, but on white-background
          sprites the honest dominant colour is the background. Mask it, which
          means keeping the image directory cell 7 deletes, if it carries an
          alpha channel, and otherwise dropping near-white pixels before
          clustering, and cluster in a perceptual space such as CIELAB so that
          F9 goes with it.
        </li>
        <li>
          <strong>Put a model in the recommendation path (F24).</strong> Once
          there is a real signal and a real encoder, score every candidate and
          rank by predicted probability instead of gating a filter on a single
          prediction. That is what the architecture in Figure&nbsp;2 describes,
          and it is a smaller change than it sounds: the filter already produces
          the candidate set.
        </li>
        <li>
          <strong>Report a baseline.</strong> Any accuracy should be printed
          next to <code>DummyClassifier(strategy="most_frequent")</code> on the
          same split. Had that line been present, the whole of §7 would have been
          visible on the day the model was fitted.
        </li>
      </ol>

      <h2 id="s12">12. Reproducing the notebook</h2>
      <p>
        A Kaggle account with an API token at <code>~/.kaggle/kaggle.json</code>,
        and a Python 3.11 environment. Install the dependencies by hand rather
        than from the file, which does not resolve (F1, F2):
      </p>
      <pre><code>{`pip install kaggle numpy pandas matplotlib scikit-learn graphviz pydotplus \\
            tensorflow ann_visualizer pillow "webcolors<1.13"`}</code></pre>
      <p>
        Graphviz also needs the system package (<code>apt install graphviz</code>{' '}
        or equivalent) for <code>pydotplus</code> to render the tree, and{' '}
        <code>ann_visualizer</code> is the loosest pin of the set, and the
        traceback the project links from its bibliography is that library
        failing against a newer Keras. Then run the notebook top to bottom: the
        first cells download about 32&nbsp;MB of sprites and the attribute CSV,
        build <code>database.json</code>, <code>color_data.json</code>,{' '}
        <code>tags.json</code> and <code>user.json</code>, and write three{' '}
        <code>.pkl</code> files beside them.
      </p>
      <p>
        Expect different numbers from the README on every execution (F3), and
        expect the colour extraction to dominate the runtime: 819 images, each
        65 536 pixels, each clustered into five groups. To interact with the
        result, run <code>recommend_pokemon()</code> and answer the five
        prompts; per §8.2 the answers change which sprites are retrieved but not
        what any model predicts.
      </p>

      <h2>Acknowledgements</h2>
      <p>
        Joint work with Lorenzo Tuzio, supervised at CPE Lyon. The project
        follows the practical-session materials of John Samuel{' '}
        <a href="#r4">[4]</a>, which its bibliography cites. Figure&nbsp;2 is
        the architecture diagram carried at the head of the project&rsquo;s own
        README. The sprites and the attribute table are the Kaggle datasets of{' '}
        <a href="#r2">[2]</a> and <a href="#r3">[3]</a>, both CC0.
      </p>

      <h2>References</h2>
      <ol>
        <li id="r1">
          Project source, at commit <code>{COMMIT}</code>, the repository&rsquo;s
          only commit.{' '}
          <a href={REPO}>github.com/TheoTime01/Project_DataMining</a>
        </li>
        <li id="r2">
          K. V. Pratama, <em>Pokemon Images Dataset</em>, Kaggle. CC0.{' '}
          <a href="https://www.kaggle.com/datasets/kvpratama/pokemon-images-dataset">
            kaggle.com/datasets/kvpratama/pokemon-images-dataset
          </a>
        </li>
        <li id="r3">
          A. Barradas, <em>Pokemon with stats</em>, Kaggle. CC0.{' '}
          <a href="https://www.kaggle.com/datasets/abcsds/pokemon">
            kaggle.com/datasets/abcsds/pokemon
          </a>
        </li>
        <li id="r4">
          J. Samuel, <em>DataMining</em>, course materials and the reference
          architecture the project follows.{' '}
          <a href="https://github.com/johnsamuelwrites/DataMining">
            github.com/johnsamuelwrites/DataMining
          </a>
        </li>
        <li id="r5">
          F. Pedregosa et al., &ldquo;Scikit-learn: Machine Learning in
          Python&rdquo;, <em>JMLR</em> 12, pp. 2825–2830, 2011. The reference for{' '}
          <code>MiniBatchKMeans</code>, <code>LabelEncoder</code>,{' '}
          <code>DecisionTreeClassifier</code> and{' '}
          <code>RandomForestClassifier</code>.
        </li>
        <li id="r6">
          D. Sculley, &ldquo;Web-scale k-means clustering&rdquo;,{' '}
          <em>WWW &rsquo;10</em>, pp. 1177–1178. The algorithm behind{' '}
          <code>MiniBatchKMeans</code>, and the source of the centroid ordering
          F8 relies on not existing.
        </li>
        <li id="r7">
          T. Hastie, R. Tibshirani and J. Friedman,{' '}
          <em>The Elements of Statistical Learning</em>, 2nd ed., Springer, 2009,
          §7.4, the optimism of the training error rate, which is §7.3 of this
          document in its general form.
        </li>
        <li id="r8">
          C. Bishop, <em>Pattern Recognition and Machine Learning</em>, Springer,
          2006, §1.5.1, the Bayes error rate of equation (2).
        </li>
        <li id="r9">
          <em>webcolors</em>, the CSS3 name tables behind{' '}
          <code>hex_to_name</code> and the nearest-name search of §3.{' '}
          <a href="https://webcolors.readthedocs.io/">webcolors.readthedocs.io</a>
        </li>
        <li id="r10">
          <em>LaTeX.css</em>, V. Doerig, the stylesheet this document is
          typeset with.{' '}
          <a href="https://github.com/vincentdoerig/latex-css">
            github.com/vincentdoerig/latex-css
          </a>
        </li>
      </ol>

      <p className="colophon">
        Every figure quoted above is read from the source, README and image files
        in the project repository <a href="#r1">[1]</a> at commit{' '}
        <code>{COMMIT}</code>: cell contents and line counts from{' '}
        <code>projet.ipynb</code>, the reported metrics from the README, and the
        root-node counts, tree dimensions and training curves from the PNGs
        under <code>img/</code>, which are reproduced here unaltered apart from
        rescaling. The committed notebook stores no cell outputs, so the sample
        sizes in §7.1 are recovered from the decimal expansions of the published
        metrics rather than read; the confusion counts in §7.1, the ceiling in
        Table&nbsp;3 and Figure&nbsp;5, and the reconstruction in Table&nbsp;4
        are derived, and are marked as derived where they appear. Findings
        marked † in §9 were verified by executing the construct in isolation
        against pandas 3.0.5 and scikit-learn 1.9.0; the notebook targets
        Python 3.11 and its own era of both libraries, and the behaviours
        checked, namely a missing column filling with <code>NaN</code>, a
        single-class <code>LabelEncoder</code> and alphabetical class ordering,
        are stable across that range. The Kaggle inputs were not downloaded and
        no model was refitted on the original data. Typeset with{' '}
        <a href="https://github.com/vincentdoerig/latex-css">LaTeX.css</a>{' '}
        <a href="#r10">[10]</a>.
      </p>
    </article>
  );
};

export default PokemonRecommenderProject;
